<!--
  Distributor App: Performance dashboard.

  Card: Distributor app (route fork) (https://trello.com/c/QV2U4KhF)
  Pebble: Performance trends + top products.

  Delivers what the left-nav has always claimed this page does -- "GMV, top
  products, trends" -- over a selectable date range: the four headline totals,
  a GMV trend, and a top-products ranking.

  Backend contract (consumed, not modified):
    GET /api/reporting/distributor-performance?start_date&end_date
      -> { scope: "owner"|"builder",
           stats: { total_stores, total_orders, total_gmv (dollars),
                    avg_order_value (dollars) },
           range: { start_date, end_date },   // echoed; null = unbounded
           bucket: "day"|"week"|"month",
           gmv_series: [{ bucket_start, gmv, orders }],
           top_products: [{ product_id, product_name, gmv, units }] }
  Scope is server-enforced (Owner sees all distributor stores; Builder is
  scoped to their own) -- the component just renders what it is given.

  ONE DEFINITION OF GMV. The server computes the totals, the series and the
  ranking from a single shared predicate, so this component never re-derives
  or re-sorts any of them. In particular the trend is NOT summed here to
  produce a total and the product list is NOT re-ranked client-side: doing
  either would create a second answer to a question the server already
  answered, which is precisely how a page ends up showing two different
  numbers both labelled GMV.

  CHARTING USES NO NEW DEPENDENCY. The trend is inline SVG, following the
  existing in-repo precedent (components/portal/StoreSparkline.vue). A chart
  library would be a new runtime dependency needing CTO sign-off and a
  DEPENDENCIES.md row (project principle 11), and this chart does not need one.

  EMPTY IS THE COMMON CASE. A brand-new distributor has no orders at all, so
  every derived view degrades to a legible message rather than a broken chart:
  the peak used for bar scaling is floored at 1 so nothing divides by zero,
  and no NaN can reach the DOM.
-->
<template>
    <section
        class="perf"
        data-testid="dist-reporting-performance"
    >
        <header class="perf__head">
            <h1 class="perf__title">Performance</h1>
            <p
                v-if="scope"
                class="perf__scope"
                data-testid="perf-scope"
            >
                {{ scopeLabel }}
            </p>

            <div class="perf__controls">
                <label class="perf__range-label" for="perf-range">
                    Date range
                </label>
                <!--
                  A Vuetify v-select, matching the sibling reporting pages
                  (DistributorReportsPage.vue, ReportBuilder.vue).

                  A PLAIN HTML SELECT would be undriveable by ui-runner:
                  `select_option` dispatches at an inner `input[role=combobox]`
                  (browser.py:1040-1042) and returns `no-combobox-input`
                  without one, so a scenario using it would VALIDATE and then
                  fail at runtime. `validate()` cannot catch that -- the step
                  has an executor, it just cannot drive that markup. This
                  control was one, briefly; the proven-working path is a
                  v-select, driven by visible option text
                  (tests/ui-scenarios/dist-reports-page-filters.yaml:111 does
                  exactly this against reports-store-select).

                  NOTE TO ANYONE GREPPING: this comment deliberately no longer
                  spells out the plain-HTML tag name. It used to, and a
                  reviewer grepping for that tag matched THIS COMMENT and
                  reported the component as still using one -- a comment
                  warning against a pattern reads identically to the pattern.

                  @update:modelValue, NOT @change -- Vuetify 3's @change does
                  not fire on selects (CLAUDE.md, known landmine).
                -->
                <v-select
                    id="perf-range"
                    class="perf__range-select"
                    data-testid="perf-range-select"
                    :items="rangeOptions"
                    :model-value="selectedRange"
                    item-title="label"
                    item-value="value"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    @update:model-value="onRangeChange"
                />
            </div>
        </header>

        <p
            v-if="loading"
            class="perf__loading"
            data-testid="perf-loading"
            role="status"
        >
            Loading performance stats...
        </p>

        <p
            v-if="loadError"
            class="perf__error"
            data-testid="perf-load-error"
            role="alert"
        >
            We could not load your performance stats. Please reload the page
            and try again.
        </p>

        <template v-if="!loading && !loadError">
            <div class="perf__cards">
                <div class="perf__card" data-testid="perf-stat-total_stores">
                    <span class="perf__card-value">{{ stats.total_stores }}</span>
                    <span class="perf__card-label">Total Stores</span>
                </div>
                <div class="perf__card" data-testid="perf-stat-total_orders">
                    <span class="perf__card-value">{{ stats.total_orders }}</span>
                    <span class="perf__card-label">Total Orders</span>
                </div>
                <div class="perf__card" data-testid="perf-stat-total_gmv">
                    <span class="perf__card-value">{{ formattedGmv }}</span>
                    <span class="perf__card-label">Total GMV</span>
                </div>
                <div class="perf__card" data-testid="perf-stat-avg_order_value">
                    <span class="perf__card-value">{{ formattedAov }}</span>
                    <span class="perf__card-label">Avg Order Value</span>
                </div>
            </div>

            <!-- GMV over time -->
            <section class="perf__panel">
                <header class="perf__panel-head">
                    <h2 class="perf__panel-title">GMV over time</h2>
                    <p
                        class="perf__panel-sub"
                        data-testid="perf-gmv-bucket"
                    >
                        {{ bucketLabel }}
                    </p>
                </header>

                <p
                    v-if="!hasSeries"
                    class="perf__empty"
                    data-testid="perf-gmv-empty"
                >
                    No orders in this date range yet. Once orders come in,
                    their GMV will chart here.
                </p>

                <div
                    v-else
                    class="perf__chart"
                    data-testid="perf-gmv-chart"
                >
                    <svg
                        class="perf__chart-svg"
                        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
                        preserveAspectRatio="none"
                        role="img"
                        :aria-label="chartAriaLabel"
                    >
                        <rect
                            v-for="bar in bars"
                            :key="bar.key"
                            :data-testid="`perf-gmv-bar-${bar.key}`"
                            :x="bar.x"
                            :y="bar.y"
                            :width="bar.width"
                            :height="bar.height"
                            class="perf__chart-bar"
                        >
                            <title>{{ bar.title }}</title>
                        </rect>
                    </svg>
                    <ol class="perf__chart-axis">
                        <li
                            v-for="bar in bars"
                            :key="`axis-${bar.key}`"
                            class="perf__chart-tick"
                        >
                            {{ bar.axisLabel }}
                        </li>
                    </ol>
                </div>
            </section>

            <!-- Top products -->
            <section class="perf__panel">
                <header class="perf__panel-head">
                    <h2 class="perf__panel-title">Top products</h2>
                    <p
                        class="perf__panel-sub"
                        data-testid="perf-top-products-ranked-by"
                    >
                        Ranked by GMV
                    </p>
                </header>

                <p
                    v-if="!hasTopProducts"
                    class="perf__empty"
                    data-testid="perf-top-products-empty"
                >
                    No products sold in this date range yet.
                </p>

                <ol v-else class="perf__products">
                    <li
                        v-for="(product, index) in topProducts"
                        :key="product.product_id"
                        class="perf__product"
                        :data-testid="`perf-top-product-${product.product_id}`"
                    >
                        <span class="perf__product-rank">{{ index + 1 }}</span>
                        <span class="perf__product-name">
                            {{ product.product_name }}
                        </span>
                        <span class="perf__product-gmv">
                            {{ formatCurrency(product.gmv) }}
                        </span>
                        <span class="perf__product-units">
                            {{ product.units }} units
                        </span>
                    </li>
                </ol>
            </section>
        </template>
    </section>
</template>

<script>
import { getDistributorPerformance } from "@/services/distributorService";
import { formatCurrency } from "@/utils/currency";

// Chart viewBox geometry. The SVG stretches to its container via
// preserveAspectRatio=none; these are internal coordinate units.
const CHART_W = 720;
const CHART_H = 180;
// Gap between columns, in the same units. Bars are sized off the remainder.
const BAR_GAP = 6;

// Range options. "all" is FIRST and is the default -- a distributor who never
// touches this control sees exactly what this page showed before the filter
// existed. The rest are rolling windows ending today, which is what "last 30
// days" means to a person reading a dashboard.
const RANGE_OPTIONS = [
    { value: "all", label: "All time", days: null },
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "30d", label: "Last 30 days", days: 30 },
    { value: "90d", label: "Last 90 days", days: 90 },
    { value: "365d", label: "Last 12 months", days: 365 },
];

// Bucket name -> the phrase shown under the chart title. The server picks the
// bucket from the span; the client only names it, so the axis is never
// ambiguous about what one column represents.
const BUCKET_LABELS = {
    day: "Daily buckets",
    week: "Weekly buckets",
    month: "Monthly buckets",
};

/**
 * Format a YYYY-MM-DD as a compact axis tick.
 *
 * Parsed as a LOCAL date rather than via `new Date("YYYY-MM-DD")`, which
 * JavaScript reads as UTC midnight -- that shifts the label back a day for
 * anyone west of Greenwich and would print the wrong date under a correct bar.
 */
function axisLabelFor(isoDate, bucket) {
    const parts = String(isoDate || "").split("-");
    if (parts.length !== 3) return isoDate || "";
    const [year, month, day] = parts.map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return isoDate;
    if (bucket === "month") {
        return date.toLocaleDateString(undefined, {
            month: "short",
            year: "2-digit",
        });
    }
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}

/** Today, as YYYY-MM-DD in the viewer's own timezone. */
function todayIso() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
    )}`;
}

/** `days` before today, as YYYY-MM-DD. */
function daysAgoIso(days) {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1));
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
    )}`;
}

export default {
    name: "PerformanceDashboard",
    data() {
        return {
            scope: "",
            stats: {
                total_stores: 0,
                total_orders: 0,
                total_gmv: 0,
                avg_order_value: 0,
            },
            bucket: "day",
            gmvSeries: [],
            topProducts: [],
            selectedRange: "all",
            rangeOptions: RANGE_OPTIONS,
            chartWidth: CHART_W,
            chartHeight: CHART_H,
            loading: true,
            loadError: false,
        };
    },
    computed: {
        formattedGmv() {
            return formatCurrency(this.stats.total_gmv);
        },
        formattedAov() {
            return formatCurrency(this.stats.avg_order_value);
        },
        scopeLabel() {
            return this.scope === "owner"
                ? "Showing all your stores"
                : "Showing your stores";
        },
        bucketLabel() {
            return BUCKET_LABELS[this.bucket] || BUCKET_LABELS.day;
        },
        hasSeries() {
            return this.gmvSeries.length > 0;
        },
        hasTopProducts() {
            return this.topProducts.length > 0;
        },
        /**
         * The peak bucket GMV, used as the bar-scaling denominator.
         *
         * Floored at 1 so a range whose buckets are all $0 (real: a store
         * running an all-free promotion) cannot divide by zero and put NaN
         * in the DOM. Scaling to the PEAK BUCKET rather than to the total is
         * what makes the tallest column fill the chart.
         */
        peakGmv() {
            return Math.max(1, ...this.gmvSeries.map((p) => Number(p.gmv) || 0));
        },
        bars() {
            const count = this.gmvSeries.length;
            if (!count) return [];
            const slot = this.chartWidth / count;
            const width = Math.max(1, slot - BAR_GAP);
            return this.gmvSeries.map((point, index) => {
                const gmv = Number(point.gmv) || 0;
                const height = Math.max(
                    0,
                    (gmv / this.peakGmv) * this.chartHeight
                );
                return {
                    key: point.bucket_start,
                    x: index * slot + BAR_GAP / 2,
                    y: this.chartHeight - height,
                    width,
                    height,
                    axisLabel: axisLabelFor(point.bucket_start, this.bucket),
                    title: `${point.bucket_start}: ${formatCurrency(gmv)} over ${
                        point.orders
                    } orders`,
                };
            });
        },
        chartAriaLabel() {
            return `GMV by ${this.bucket}, ${this.gmvSeries.length} buckets, peak ${formatCurrency(
                this.peakGmv
            )}`;
        },
    },
    created() {
        this.loadStats();
    },
    methods: {
        formatCurrency,
        /**
         * Translate the selected option into the wire's date bounds.
         *
         * "All time" returns an EMPTY object so no bound is sent at all --
         * an unfiltered request, byte-identical to the one this page made
         * before the control existed.
         */
        rangeParams() {
            const option = RANGE_OPTIONS.find(
                (candidate) => candidate.value === this.selectedRange
            );
            if (!option || option.days === null) return {};
            return {
                startDate: daysAgoIso(option.days),
                endDate: todayIso(),
            };
        },
        /**
         * Apply a newly chosen range and refetch.
         *
         * Split out rather than inlined on the control so the handler is
         * testable and so the model update and the refetch cannot drift
         * apart -- v-select is not v-model'd here precisely so the refetch
         * is never skipped by a silent model-only update.
         */
        onRangeChange(value) {
            this.selectedRange = value;
            return this.loadStats();
        },
        async loadStats() {
            this.loading = true;
            this.loadError = false;
            try {
                const response = await getDistributorPerformance(
                    this.rangeParams()
                );
                const data = response ? response.data : null;
                if (data) {
                    this.scope = data.scope || "";
                    if (data.stats) {
                        this.stats = data.stats;
                    }
                    // Defensive defaults: an older cached bundle or a partial
                    // response must leave the four totals rendering rather
                    // than blanking the page or emitting NaN.
                    this.bucket = data.bucket || "day";
                    this.gmvSeries = Array.isArray(data.gmv_series)
                        ? data.gmv_series
                        : [];
                    // Rendered in the order the server ranked them. NOT
                    // re-sorted here -- the server ranks by GMV, and a
                    // client-side sort is a second ranking to disagree with.
                    this.topProducts = Array.isArray(data.top_products)
                        ? data.top_products
                        : [];
                }
            } catch (e) {
                this.loadError = true;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.perf {
    max-width: 960px;
    margin: var(--tf-sp-8, 32px) auto;
    padding: 0 var(--tf-sp-4, 16px);
    color: var(--tf-fg-2, var(--tf-fg-2));
}
.perf__title {
    font-size: var(--tf-size-h2, 1.5rem);
    font-weight: 600;
    color: var(--tf-fg-1, var(--tf-fg-1));
    margin-bottom: var(--tf-sp-2, 8px);
}
.perf__scope {
    color: var(--tf-fg-3, var(--tf-fg-4));
    margin-bottom: var(--tf-sp-4, 16px);
}
.perf__controls {
    display: flex;
    align-items: center;
    gap: var(--tf-sp-2, 8px);
    margin-bottom: var(--tf-sp-6, 24px);
}
.perf__range-label {
    font-size: var(--tf-size-body-sm, 0.875rem);
    color: var(--tf-fg-3, var(--tf-fg-4));
}
.perf__range-select {
    padding: var(--tf-sp-2, 8px);
    border: 1px solid var(--tf-border, var(--tf-border));
    border-radius: var(--tf-radius-sm, 8px);
    background: var(--tf-surface, var(--tf-bg));
    color: var(--tf-fg-1, var(--tf-fg-1));
}
.perf__loading {
    font-style: italic;
}
.perf__error {
    color: var(--tf-error, var(--tf-red));
    font-weight: 600;
}
.perf__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--tf-sp-4, 16px);
    margin-top: var(--tf-sp-4, 16px);
}
.perf__card {
    display: flex;
    flex-direction: column;
    gap: var(--tf-sp-2, 8px);
    padding: var(--tf-sp-5, 20px);
    border: 1px solid var(--tf-border, var(--tf-border));
    border-radius: var(--tf-radius-md, 12px);
    background: var(--tf-surface, var(--tf-bg));
}
.perf__card-value {
    font-size: var(--tf-size-h1, 2rem);
    font-weight: 700;
    color: var(--tf-fg-1, var(--tf-fg-1));
}
.perf__card-label {
    font-size: var(--tf-size-body-sm, 0.875rem);
    color: var(--tf-fg-3, var(--tf-fg-4));
}
.perf__panel {
    margin-top: var(--tf-sp-8, 32px);
    padding: var(--tf-sp-5, 20px);
    border: 1px solid var(--tf-border, var(--tf-border));
    border-radius: var(--tf-radius-md, 12px);
    background: var(--tf-surface, var(--tf-bg));
}
.perf__panel-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--tf-sp-4, 16px);
    margin-bottom: var(--tf-sp-4, 16px);
}
.perf__panel-title {
    font-size: var(--tf-size-h3, 1.125rem);
    font-weight: 600;
    color: var(--tf-fg-1, var(--tf-fg-1));
}
.perf__panel-sub {
    font-size: var(--tf-size-body-sm, 0.875rem);
    color: var(--tf-fg-3, var(--tf-fg-4));
}
.perf__empty {
    color: var(--tf-fg-3, var(--tf-fg-4));
    font-style: italic;
}
.perf__chart-svg {
    display: block;
    width: 100%;
    height: 180px;
}
.perf__chart-bar {
    fill: var(--tf-primary, #119ae9);
}
.perf__chart-axis {
    display: flex;
    justify-content: space-between;
    list-style: none;
    margin: var(--tf-sp-2, 8px) 0 0;
    padding: 0;
    font-size: var(--tf-size-body-xs, 0.75rem);
    color: var(--tf-fg-3, var(--tf-fg-4));
}
.perf__chart-tick {
    flex: 1 1 0;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
}
.perf__products {
    list-style: none;
    margin: 0;
    padding: 0;
}
.perf__product {
    display: grid;
    grid-template-columns: 2rem 1fr auto auto;
    align-items: center;
    gap: var(--tf-sp-4, 16px);
    padding: var(--tf-sp-3, 12px) 0;
    border-bottom: 1px solid var(--tf-border, var(--tf-border));
}
.perf__product:last-child {
    border-bottom: none;
}
.perf__product-rank {
    color: var(--tf-fg-3, var(--tf-fg-4));
    font-variant-numeric: tabular-nums;
}
.perf__product-name {
    color: var(--tf-fg-1, var(--tf-fg-1));
}
.perf__product-gmv {
    font-weight: 600;
    color: var(--tf-fg-1, var(--tf-fg-1));
    font-variant-numeric: tabular-nums;
}
.perf__product-units {
    color: var(--tf-fg-3, var(--tf-fg-4));
    font-size: var(--tf-size-body-sm, 0.875rem);
    font-variant-numeric: tabular-nums;
}
</style>
